import {Controller} from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["summary", "platformFees", "processingFees", "tax", "total", "totalCurrencySymbol", "checkoutBtn",
    "orderSummaryWrapper", "dropdownIcon", "fullPrice", "discount", "discountWrapper", "fullPriceWrapper",
    "platformFeesWrapper", "processingFeesWrapper", 'taxWrapper', "emptyState", "orderTotals"];

  static values = {
    taxFee: Number,
    eventPublished: Boolean,
    // Fee rates sourced from config/initializers/constants.rb via the view, so
    // the buyer-facing summary always matches what the backend charges.
    stubFeePercent: {type: Number, default: 0.03},
    stubFeeDollar: {type: Number, default: 1},
    stripeFeePercent: {type: Number, default: 0.029},
    stripeFeeCents: {type: Number, default: 0.30},
  };

  connect() {
    this.tickets = {};
    // ticketId -> day label (e.g. "Tuesday, Jul 14") for day-scoped tickets, so
    // the summary can disambiguate identically-named per-day lines (#32). Set by
    // the ticket-quantity controllers; survives summary re-renders.
    this.ticketDays = this.ticketDays || {};
    // Set up age verification checkbox listener if present
    const ageVerificationCheckbox = document.getElementById('age_verified_checkbox');
    if (ageVerificationCheckbox) {
      ageVerificationCheckbox.addEventListener('change', () => {
        this.updateCheckoutButton();
      });
    }

    // Set initial empty-state / totals visibility before any ticket is added.
    this.refreshSummary();
  }

  update(ticketId, name, price, quantity, absorbFee, discountedPrice = 0, discounted, displayPrice, meetsMinRequirement = true, purchaseLimitsEnabled = false, minTicketsPerOrder = 0, discountType = 'none', discountId = null, isDonation = false) {
    const finalPrice = price - discountedPrice < 0 ? 0 : price - discountedPrice

    if (quantity > 0) {
      this.tickets[ticketId] = {
        name,
        price,
        final_price: finalPrice,
        quantity,
        absorbFee,
        tax: isDonation ? 0 : this.calculateTax(finalPrice, quantity),
        platformFee: absorbFee ? 0 : this.calculatePlatformFee(finalPrice, quantity),
        discounted,
        displayPrice,
        discountedPrice,
        meetsMinRequirement,
        purchaseLimitsEnabled,
        minTicketsPerOrder,
        discount_type: discountType,
        discount_id: discountId,
        isDonation
      };
    } else {
      delete this.tickets[ticketId];
    }

    this.refreshSummary();
  }

  refreshSummary() {
    let subtotal = 0;
    let platformFees = 0;
    let passOnFeeAmount = 0;
    let tax = 0;
    let fullPrice = 0;
    let discountPrice = 0;

    if (this.hasSummaryTarget) this.summaryTargets.forEach(target => {
      target.innerHTML = ""
    })

    Object.entries(this.tickets).forEach(([ticketId, ticket]) => {
      const {
        name,
        quantity,
        final_price,
        absorbFee,
        platformFee,
        tax: ticketTax,
        discounted,
        displayPrice,
        discountedPrice
      } = ticket;

      const ticketTotal = final_price * quantity;

      subtotal += ticketTotal;
      platformFees += platformFee;
      tax += ticketTax;
      fullPrice += displayPrice * quantity
      discountPrice += discountedPrice * quantity

      if (!absorbFee) {
        passOnFeeAmount += ticketTotal + ticketTax + platformFee;
      }

      if (this.hasSummaryTarget) {
        const div = document.createElement("div");
        // Append the day for day-scoped tickets so identically-named per-day
        // lines are distinguishable (e.g. "2 x General Admission (Tue, Jul 14)").
        const day = this.ticketDays && this.ticketDays[ticketId];
        div.textContent = day ? `${quantity} x ${name} (${day})` : `${quantity} x ${name}`;
        div.classList.add("summary-item");

        const ticketTotalDiv = document.createElement("div");
        if (ticketTotal > 0) {
          ticketTotalDiv.textContent = `$${ticketTotal.toFixed(2)}`;
        } else {
          ticketTotalDiv.textContent = "Free";
        }

        div.appendChild(ticketTotalDiv);

        this.summaryTargets.forEach((target, index) => {
          target.appendChild(index === 0 ? div : div.cloneNode(true));
        });
      }
    });

    const hasTickets = Object.keys(this.tickets).length > 0;
    const shouldChargeProcessingFee = passOnFeeAmount > 0;
    const processingFees = shouldChargeProcessingFee ? ((passOnFeeAmount + this.stripeFeeCentsValue) / (1 - this.stripeFeePercentValue)) - passOnFeeAmount : 0;
    const total = subtotal + platformFees + processingFees + tax;

    // Drive item-dependent UI off the authoritative ticket count rather than a
    // single summary target's innerHTML — there can be multiple summary targets
    // (desktop + mobile) and the first one may not be the populated one.
    const hasLineItems = Object.keys(this.tickets).length > 0
    if (this.hasSummaryTarget) {
      const hasAdjustments = platformFees > 0 || processingFees > 0 || tax > 0 || discountPrice > 0
      const showSubtotal = hasLineItems && hasAdjustments
      this.fullPriceWrapperTargets.forEach(target => {
        showSubtotal ? target.classList.remove('d-none') : target.classList.add('d-none')
      })
    }

    // Empty-state placeholder (cart icon + "Your order is empty") shows until
    // the buyer adds a ticket; the totals block hides while the order is empty.
    if (this.hasEmptyStateTarget) {
      this.emptyStateTargets.forEach(target => {
        hasLineItems ? target.classList.add('d-none') : target.classList.remove('d-none')
      })
    }
    if (this.hasOrderTotalsTarget) {
      this.orderTotalsTargets.forEach(target => {
        hasLineItems ? target.classList.remove('d-none') : target.classList.add('d-none')
      })
    }
    if (this.hasPlatformFeesTarget) {
      this.platformFeesWrapperTargets.forEach(target => {
        (platformFees > 0) ? target.classList.remove('d-none') : target.classList.add('d-none')
      })

      this.platformFeesTargets.forEach(target => {
        target.textContent = platformFees.toFixed(2)
      })
    }
    if (this.hasProcessingFeesTarget) {
      this.processingFeesWrapperTargets.forEach(target => {
        (processingFees > 0) ? target.classList.remove('d-none') : target.classList.add('d-none')
      })
      this.processingFeesTargets.forEach(target => {
        target.textContent = processingFees.toFixed(2)
      })
    }
    if (this.hasTaxTarget) {
      this.taxWrapperTargets.forEach(target => {
        (tax > 0) ? target.classList.remove('d-none') : target.classList.add('d-none')
      })
      this.taxTargets.forEach(target => {
        target.textContent = tax.toFixed(2)
      })
    }
    if (this.hasTotalTarget) this.totalTargets.forEach(target => {
      target.textContent = total > 0 ? total.toFixed(2) : "Free"
    })
    if (this.hasTotalCurrencySymbolTarget) this.totalCurrencySymbolTargets.forEach(target => {
      target.style.display = total > 0 ? "" : "none"
    })
    if (this.hasFullPriceTarget) this.fullPriceTargets.forEach(target => {
      target.innerText = fullPrice.toFixed(2)
    })
    if (this.hasDiscountTarget) this.discountTargets.forEach(target => {
      target.innerText = discountPrice.toFixed(2)
    })
    if (this.hasDiscountWrapperTarget) {
      this.discountWrapperTargets.forEach(target => {
        discountPrice > 0 ? target.classList.remove('d-none') : target.classList.add('d-none')
      })
    }

    this.updateCheckoutButton();
  }

  updateCheckoutButton() {
    if (!this.hasCheckoutBtnTarget) return;

    const hasTickets = Object.keys(this.tickets).length > 0;
    const ageVerificationCheckbox = document.getElementById('age_verified_checkbox');
    const isAgeVerified = !ageVerificationCheckbox || ageVerificationCheckbox.checked;
    const isEventPublished = this.eventPublishedValue !== false;

    // Check if all tickets meet their minimum purchase requirements
    const allTicketsMeetMinimumRequirements = Object.values(this.tickets).every(ticket =>
      ticket.meetsMinRequirement !== false
    );

    const shouldEnableCheckout = hasTickets && isAgeVerified && isEventPublished && allTicketsMeetMinimumRequirements;

    this.checkoutBtnTarget.disabled = !shouldEnableCheckout;
    this.checkoutBtnTarget.classList.toggle(
      "checkout-btn-disabled",
      !shouldEnableCheckout
    );

    // Update visual feedback for minimum requirements
    this.updateMinimumRequirementFeedback(allTicketsMeetMinimumRequirements);
  }

  updateMinimumRequirementFeedback(allMet) {
    if (!this.hasCheckoutBtnTarget) return;

    // Find or create feedback message element
    let feedbackElement = document.getElementById('minimum-requirement-message');

    if (!allMet && Object.keys(this.tickets).length > 0) {
      // Show feedback about minimum requirements not met
      if (!feedbackElement) {
        feedbackElement = document.createElement('div');
        feedbackElement.id = 'minimum-requirement-message';
        feedbackElement.className = 'alert alert-warning mt-2 text-sm';
        this.checkoutBtnTarget.parentElement.insertBefore(feedbackElement, this.checkoutBtnTarget.nextSibling);
      }

      // Build message listing tickets that don't meet minimum
      const ticketsNotMeetingMin = Object.values(this.tickets).filter(ticket =>
        ticket.meetsMinRequirement === false && ticket.purchaseLimitsEnabled && ticket.minTicketsPerOrder > 0
      );

      if (ticketsNotMeetingMin.length > 0) {
        const ticketMessages = ticketsNotMeetingMin.map(ticket =>
          `${ticket.name} requires a minimum of ${ticket.minTicketsPerOrder} ticket(s)`
        ).join(', ');
        feedbackElement.textContent = `Minimum purchase requirements not met: ${ticketMessages}`;
        feedbackElement.classList.remove('d-none');
      }
    } else {
      // Remove feedback message if all requirements are met
      if (feedbackElement) {
        feedbackElement.classList.add('d-none');
      }
    }
  }

  toggleCheckoutButton() {
    this.updateCheckoutButton();
  }

  calculateTax(price, quantity = 1) {
    return ((price * this.taxFeeValue) / 100) * quantity;
  }

  calculatePlatformFee(price, quantity = 1) {
    if (price == 0) return 0
    return (price * this.stubFeePercentValue) * quantity + this.stubFeeDollarValue * quantity;
  }

  showHideSummary() {
    this.orderSummaryWrapperTarget.classList.toggle('d-none')
    this.dropdownIconTarget.classList.toggle('dropdown-img')
  }

  // Get tickets data formatted for submission to backend
  // Returns object with new format: { ticketId: { quantity, discount_type, discount_id } }
  getTicketsForSubmission() {
    const formattedTickets = {};
    
    Object.keys(this.tickets).forEach(ticketId => {
      const ticket = this.tickets[ticketId];
      formattedTickets[ticketId] = {
        quantity: ticket.quantity,
        discount_type: ticket.discount_type || 'none',
        discount_id: ticket.discount_id || null
      };
    });
    
    return formattedTickets;
  }
}
